import { useEffect, useCallback, useRef } from "react";

const useHomeRteWithCursor = ({ editorRef, value, setValue }) => {
  const cursorRangeRef = useRef(null);

  const storeCursorPosition = useCallback(() => {
    const editor = editorRef.current?.editor;
    if (!editor) return;

    // Try Tiptap API first (if available)
    if (editor.commands && editor.state) {
      try {
        const { selection } = editor.state;
        const { $from, $to } = selection;

        // Convert Tiptap positions to DOM range for compatibility
        if (editor.view) {
          const startPos = editor.view.domAtPos($from.pos);
          const endPos = editor.view.domAtPos($to.pos);

          const range = document.createRange();
          range.setStart(startPos.node, startPos.offset);
          range.setEnd(endPos.node, endPos.offset);
          cursorRangeRef.current = range;
          return;
        }
      } catch (e) {
        // Fallback to compatibility API
      }
    }

    // Fallback: use compatibility API (Tiptap wrapper)
    if (editor.core?.getRange) {
      const range = editor.core.getRange();
      if (range) {
        cursorRangeRef.current = range.cloneRange();
      }
    }
  }, [editorRef]);

  const addRecordedTranscript = useCallback(
    (text) => {
      const editor = editorRef.current?.editor;
      if (!editor) return;

      // Try Tiptap API first
      if (editor.commands && editor.insertContent) {
        try {
          // If we have a stored cursor position, restore it first
          if (cursorRangeRef.current) {
            const sel = window.getSelection();
            sel.removeAllRanges();
            sel.addRange(cursorRangeRef.current);

            // Sync with Tiptap selection
            try {
              const startPos = editor.view.posAtDOM(
                cursorRangeRef.current.startContainer,
                cursorRangeRef.current.startOffset
              );
              const endPos = editor.view.posAtDOM(
                cursorRangeRef.current.endContainer,
                cursorRangeRef.current.endOffset
              );
              editor.commands.setTextSelection({ from: startPos, to: endPos });
            } catch (e) {
              // Browser selection is set even if Tiptap sync fails
            }
          }

          // Insert text using Tiptap
          editor.commands.insertContent(text);

          // Update cursor position after insertion
          storeCursorPosition();

          // Update parent state
          const newContent = editor.getHTML
            ? editor.getHTML()
            : editor.getContents();
          setValue(newContent);
          return;
        } catch (e) {
          // Fallback to DOM-based insertion
        }
      }

      // Fallback: DOM-based insertion (for compatibility)
      const sel = window.getSelection();
      if (cursorRangeRef.current) {
        sel.removeAllRanges();
        sel.addRange(cursorRangeRef.current);
        const range = cursorRangeRef.current;
        range.deleteContents();
        const textNode = document.createTextNode(text);
        range.insertNode(textNode);
        const newRange = document.createRange();
        newRange.setStart(textNode, textNode.length);
        newRange.collapse(true);
        sel.removeAllRanges();
        sel.addRange(newRange);
        cursorRangeRef.current = newRange;
      } else {
        // Insert at end using Tiptap or fallback
        if (editor.insertHTML) {
          editor.insertHTML(text);
        } else if (editor.commands) {
          editor.commands.insertContent(text);
        } else {
          // Last resort: use execCommand
          const wysiwyg =
            editor.core?.context?.element?.wysiwyg ||
            editor.view?.dom?.querySelector(".ProseMirror");
          if (wysiwyg) {
            wysiwyg.focus();
            document.execCommand("insertText", false, text);
          }
        }
      }

      // Update parent state
      const newContent = editor.getHTML
        ? editor.getHTML()
        : editor.getContents
        ? editor.getContents()
        : editor.core?.context?.element?.wysiwyg?.innerHTML || "";
      if (newContent) {
        setValue(newContent);
      }
    },
    [editorRef, setValue, storeCursorPosition]
  );

  useEffect(() => {
    const editor = editorRef.current?.editor;
    if (!editor) return;

    // Get the editor DOM element (Tiptap or compatibility wrapper)
    const wysiwyg =
      editor.view?.dom?.querySelector(".ProseMirror") ||
      editor.core?.context?.element?.wysiwyg ||
      editor.view?.dom;

    if (!wysiwyg) return;

    const handleClick = () => {
      storeCursorPosition();
    };

    const handleKeyUp = () => {
      storeCursorPosition();
    };

    const handleKeyDown = () => {
      storeCursorPosition();
    };

    wysiwyg.addEventListener("click", handleClick);
    wysiwyg.addEventListener("keyup", handleKeyUp);
    wysiwyg.addEventListener("keydown", handleKeyDown);

    return () => {
      wysiwyg.removeEventListener("click", handleClick);
      wysiwyg.removeEventListener("keyup", handleKeyUp);
      wysiwyg.removeEventListener("keydown", handleKeyDown);
    };
  }, [editorRef, storeCursorPosition]);

  return {
    addRecordedTranscript,
    storeCursorPosition,
  };
};

export default useHomeRteWithCursor;
